import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';

const AuthContext = createContext(null);

// Use REACT_APP_BACKEND_URL from .env for all API calls
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom fetch wrapper with redirect handling
const apiFetch = async (endpoint, options = {}) => {
  // Extract responseType from options if present
  const { responseType, ...restOptions } = options;
  
  // Normalize endpoint - ensure trailing slash for all requests to match FastAPI routes
  let normalizedEndpoint = endpoint;
  
  // Add trailing slash if not present and no query params
  if (!normalizedEndpoint.includes('?')) {
    if (!normalizedEndpoint.endsWith('/')) {
      normalizedEndpoint = `${normalizedEndpoint}/`;
    }
  } else {
    // Has query params - add slash before the ?
    const [path, query] = normalizedEndpoint.split('?');
    if (!path.endsWith('/')) {
      normalizedEndpoint = `${path}/?${query}`;
    }
  }
  
  const url = `${API_BASE}${normalizedEndpoint}`;
  
  const defaultHeaders = {};
  
  // Don't set Content-Type for blob responses (let browser handle it)
  if (responseType !== 'blob') {
    defaultHeaders['Content-Type'] = 'application/json';
  }
  
  const token = localStorage.getItem('inspirotracker_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  // Use redirect: 'follow' and manually handle auth on redirect if needed
  const response = await fetch(url, {
    ...restOptions,
    redirect: 'follow',
    headers: {
      ...defaultHeaders,
      ...restOptions.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    const err = new Error(error.detail || 'Request failed');
    err.response = { data: error, status: response.status };
    throw err;
  }
  
  // Handle blob response type for file downloads
  if (responseType === 'blob') {
    return await response.blob();
  }
  
  // Handle empty response (for DELETE, etc.)
  const text = await response.text();
  if (!text) {
    return {};
  }
  
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

// Helper to build query string from params
const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  return '?' + Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
};

// API wrapper object to mimic axios interface - returns { data: ... } for compatibility
const createApi = () => ({
  get: async (url, config = {}) => {
    const { params, responseType, ...restConfig } = config;
    const queryString = buildQueryString(params);
    const result = await apiFetch(`${url}${queryString}`, { method: 'GET', responseType, ...restConfig });
    return { data: result };
  },
  post: async (url, data, config = {}) => ({ data: await apiFetch(url, { method: 'POST', body: JSON.stringify(data), ...config }) }),
  put: async (url, data, config = {}) => ({ data: await apiFetch(url, { method: 'PUT', body: JSON.stringify(data), ...config }) }),
  delete: async (url, config = {}) => ({ data: await apiFetch(url, { method: 'DELETE', ...config }) }),
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('inspirotracker_token'));
  const [loading, setLoading] = useState(true);

  // Create API instance
  const api = useMemo(() => createApi(), []);

  // Apply saved theme globally on auth init
  const applyTheme = (theme) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('inspirotracker_token');
      if (storedToken) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
          setToken(storedToken);

          // Sync auth to desktop agent on app init
          if (window.electronAPI?.isDesktop) {
            window.electronAPI.setAuth({
              token: storedToken,
              userId: response.data.id,
              userName: response.data.name,
              role: response.data.role,
              organizationId: response.data.organization_id
            }).catch(() => {});
          }
          
          // Fetch and apply saved theme
          try {
            const profileRes = await api.get('/profile');
            const apiTheme = profileRes.data.theme;
            // Only override localStorage if the API has an explicit theme saved
            if (apiTheme) {
              localStorage.setItem('inspirotracker_theme', apiTheme);
              applyTheme(apiTheme);
            } else {
              // Use localStorage (user's last choice) or fall back to system
              applyTheme(localStorage.getItem('inspirotracker_theme') || 'system');
            }
          } catch (e) {
            // Apply from localStorage cache if API fails
            applyTheme(localStorage.getItem('inspirotracker_theme') || 'system');
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('inspirotracker_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    // Apply cached theme immediately (before API call) to prevent flash
    const cachedTheme = localStorage.getItem('inspirotracker_theme');
    if (cachedTheme) applyTheme(cachedTheme);

    initAuth();

    return () => {};
  }, [api]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('inspirotracker_token', newToken);
    setToken(newToken);
    setUser(userData);

    // Sync auth to desktop agent if running inside Electron
    if (window.electronAPI?.isDesktop) {
      window.electronAPI.setAuth({
        token: newToken,
        userId: userData.id,
        userName: userData.name,
        role: userData.role,
        organizationId: userData.organization_id
      }).catch(() => {});
    }

    return userData;
  };

  const register = async (name, email, password, organizationName) => {
    const response = await api.post('/auth/register', {
      name,
      email,
      password,
      organization_name: organizationName || null
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('inspirotracker_token', newToken);
    setToken(newToken);
    setUser(userData);

    // Sync auth to desktop agent if running inside Electron
    if (window.electronAPI?.isDesktop) {
      window.electronAPI.setAuth({
        token: newToken,
        userId: userData.id,
        userName: userData.name,
        role: userData.role,
        organizationId: userData.organization_id
      }).catch(() => {});
    }

    return userData;
  };

  const logout = async () => {
    // Stop any running timer before logging out
    try {
      await api.post('/time-entries/stop');
    } catch (e) {
      // Ignore errors (no timer running, etc.)
    }

    // Clear auth in desktop agent if running inside Electron
    if (window.electronAPI?.isDesktop) {
      window.electronAPI.clearAuth().catch(() => {});
    }

    localStorage.removeItem('inspirotracker_token');
    setToken(null);
    setUser(null);
  };

  // Direct login with token and user data (for invitation acceptance, etc.)
  const loginWithToken = (newToken, userData) => {
    localStorage.setItem('inspirotracker_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Refresh user data from server (e.g., after profile photo update)
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      loginWithToken,
      register,
      logout,
      updateUser,
      refreshUser,
      api,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
